import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Paper,
  Stack,
  Group,
  Title,
  Tooltip,
  Text,
  Popover,
} from '@mantine/core';
import { closeAllModals, modals, openModal } from '@mantine/modals';
import RemitSettingModal from '../components/modal/RemitSettingModal.tsx';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import useSiteProviders from '../hooks/useSiteProviders.ts';
import useRemitSettings from '../hooks/useRemitSettings.ts';
import { RemitSetting } from '../models/remit-setting.ts';
import { RemitSettingType } from '../models/remit-setting-type.ts';
import { RemittanceFeeMode } from '../models/remittance-fee-mode.ts';
import useSiteProviderStores from '../hooks/useSiteProviderStores.ts';
import useVoidRemitSetting from '../hooks/useVoidRemitSetting.ts';
import { notifications } from '@mantine/notifications';

export default function RemitSettingListPage() {
  const { siteProviderId } = useParams();
  const { isLoading: isRemitSettingLoading, data: remitSettings } =
    useRemitSettings(siteProviderId);
  const { isLoading: isSiteProviderLoading, data: siteProviders = [] } =
    useSiteProviders();
  const { data: stores } = useSiteProviderStores(siteProviderId);
  const siteProvider = useMemo(() => {
    return (
      siteProviders.find((v) => v.id === siteProviderId) || { id: '', name: '' }
    );
  }, [siteProviders, siteProviderId]);
  const { mutate: voidRemitSetting } = useVoidRemitSetting();

  // 開啟新增/編輯 Modal
  const openForm = (remitSetting = null) => {
    openModal({
      title: remitSetting ? '編輯匯款帳號' : '新增匯款帳號',
      children: (
        <RemitSettingModal
          siteProvider={siteProvider}
          stores={stores}
          remitSetting={remitSetting}
          hasDefault={remitSettings.length > 0}
          onSave={() => {
            closeAllModals();
          }}
          onCancel={() => closeAllModals()}
        />
      ),
    });
  };

  // 刪除帳號
  const handleDelete = (row: RemitSetting) => {
    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: `刪除匯款帳號`,
      innerProps: {
        modalBody: `確定要刪除匯款帳號 ${row.bankAccount.accountName} 嗎？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });
          voidRemitSetting(row.id, {
            onSuccess: () => {
              notifications.show({
                title: '成功',
                message: `匯款帳號 ${row.bankAccount.accountName} 已刪除`,
                color: 'green',
              });
              closeAllModals();
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  };

  const columns: DataTableColumn<RemitSetting>[] = [
    {
      title: '類型',
      accessor: 'type',
      render: (row) => {
        switch (row.type) {
          case RemitSettingType.DEFAULT:
            return (
              <Button variant="transparent" disabled size="sm">
                預設匯款帳號
              </Button>
            );
          case RemitSettingType.SPECIFIC:
            return (
              <Popover position="bottom" withArrow shadow="md">
                <Popover.Target>
                  <Button variant="transparent" size="sm">
                    指定停車場
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  {row.specifyParkingLotIds.map((x) => {
                    const store = stores.find((s) => s.id === x);
                    return (
                      <Text key={x} size="xs">
                        {store ? store.name : '未知停車場'}
                      </Text>
                    );
                  })}
                </Popover.Dropdown>
              </Popover>
            );
          default:
            return '未知類型';
        }
      },
    },
    {
      title: '銀行',
      accessor: 'bankName',
      render: ({ bankAccount }) => bankAccount.bankName,
    },
    {
      title: '分行',
      accessor: 'bankBranchName',
      render: ({ bankAccount }) => bankAccount.bankBranchName,
    },
    {
      title: '銀行代碼',
      accessor: 'fullBankCode',
      render: ({ bankAccount }) => bankAccount.fullBankCode,
    },
    {
      title: '帳號',
      accessor: 'accountNumber',
      render: ({ bankAccount }) => bankAccount.accountNumber,
    },
    {
      title: '戶名',
      accessor: 'accountName',
      render: ({ bankAccount }) => bankAccount.accountName,
    },
    {
      title: '手續費模式',
      accessor: 'feeMode',
      render: ({ remittanceFeeMode }) =>
        remittanceFeeMode === RemittanceFeeMode.BEN ? '內扣' : '不內扣',
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (row) => (
        <Group>
          <Button size="xs" variant="outline" onClick={() => openForm(row)}>
            編輯
          </Button>
          {row.type === RemitSettingType.DEFAULT && (
            <Tooltip label="預設匯款帳務不能刪除">
              <Button size="xs" color="red" variant="outline" disabled={true}>
                刪除
              </Button>
            </Tooltip>
          )}
          {row.type !== RemitSettingType.DEFAULT && (
            <Button
              size="xs"
              color="red"
              variant="outline"
              onClick={() => handleDelete(row)}
            >
              刪除
            </Button>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Paper p="xl" radius="lg" shadow="xl" mb="xl" mt="md">
      <Stack gap="xl">
        <Group justify="space-between" align="center" mb="md">
          <Title order={4}>{siteProvider.name}的匯款帳號</Title>
          <Button onClick={() => openForm()}>新增</Button>
        </Group>
        <DataTable
          idAccessor="id"
          highlightOnHover
          minHeight={150}
          columns={columns}
          records={remitSettings}
          verticalSpacing="xs"
          fetching={isRemitSettingLoading || isSiteProviderLoading}
          noRecordsText="尚未設定匯款帳號"
        />
      </Stack>
    </Paper>
  );
}
